import React from "react";
import {
  Box,
  VStack,
  HStack,
  Icon,
  Text,
  Collapse,
  useDisclosure,
  useColorModeValue,
  Button,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { 
  FiHome,
  FiUsers,
  FiDatabase,
  FiSettings,
  FiActivity,
  FiFileText,
  FiChevronDown,
  FiChevronRight,
  FiShield,
  FiGrid,
  FiUser,
  FiRefreshCw,
  FiMapPin,
  FiBriefcase,
  FiClock,
  FiLogIn,
  FiBarChart2,
  FiArrowLeft,
} from "react-icons/fi";
import { ADMIN_THEME_CONFIG } from "../config";
import { useLocation, useNavigate } from "react-router-dom";
import { useCanAccess } from "../../rbac/useCanAccess";

interface SidebarItemProps {
  icon: React.ElementType;
  label: string;
  isActive?: boolean;
  badge?: number;
  onClick?: () => void;
}

interface SidebarGroupProps {
  title: string;
  icon: React.ElementType;
  items: SidebarItemProps[];
  defaultIsOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ 
  icon, 
  label, 
  isActive = false, 
  badge,
  onClick 
}) => {
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const activeBg = useColorModeValue("blue.50", "blue.900");
  const activeColor = useColorModeValue("blue.600", "blue.200");
  const textColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Button
      variant="ghost"
      justifyContent="flex-start"
      w="100%"
      h="auto"
      p={3}
      bg={isActive ? activeBg : "transparent"}
      color={isActive ? activeColor : textColor}
      _hover={{ bg: isActive ? activeBg : hoverBg }}
      onClick={onClick}
      borderRadius="lg"
    >
      <HStack spacing={3} w="100%">
        <Icon as={icon} boxSize={5} />
        <Text fontSize="sm" fontWeight={isActive ? "semibold" : "medium"} flex="1">
          {label}
        </Text>
        {badge && badge > 0 && (
          <Badge 
            colorScheme="red" 
            variant="solid" 
            borderRadius="full"
            fontSize="xs"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {badge > 99 ? '99+' : badge}
          </Badge>
        )}
      </HStack>
    </Button>
  );
};

const SidebarGroup: React.FC<SidebarGroupProps> = ({ 
  title, 
  icon, 
  items, 
  defaultIsOpen = false 
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen });
  const textColor = useColorModeValue("gray.600", "gray.400");
  const headerColor = useColorModeValue("gray.800", "gray.100");

  return (
    <VStack align="stretch" spacing={2}>
      <Button
        variant="ghost"
        justifyContent="flex-start"
        w="100%"
        h="auto"
        p={3}
        color={headerColor}
        fontWeight="semibold"
        fontSize="sm"
        onClick={onToggle}
        borderRadius="lg"
        _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}
      >
        <HStack spacing={3} w="100%">
          <Icon as={icon} boxSize={5} />
          <Text flex="1">{title}</Text>
          <Icon 
            as={isOpen ? FiChevronDown : FiChevronRight} 
            boxSize={4}
            color={textColor}
          />
        </HStack>
      </Button>
      
      <Collapse in={isOpen}>
        <VStack align="stretch" spacing={1} pl={4}>
          {items.map((item) => (
            <SidebarItem key={item.label} {...item} />
          ))}
        </VStack>
      </Collapse>
    </VStack>
  );
};

interface AdminSidebarProps {
  activeItem?: string;
  onItemClick?: (item: string) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  activeItem = "dashboard", 
  onItemClick 
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccess } = useCanAccess();

  // Mock badge counts - replace with actual data
  const badgeCounts = {
    inspections: 15,
    categories: 0,
    employees: 5,
    loginRequests: 8, // New pending login requests
    auditLogs: 0, // No badge for audit logs by default
  };

  const handleItemClick = (item: string) => {
    onItemClick?.(item);
  };

  return (
    <Box
      w="280px"
      h="100%"
      bg={bg}
      borderRight="1px solid"
      borderColor={borderColor}
      p={4}
      overflowY="auto"
    >
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <Box>
          <Text 
            fontSize="xl" 
            fontWeight="bold" 
            color={ADMIN_THEME_CONFIG.COLORS.PRIMARY}
            mb={1}
          >
            Admin Panel
          </Text>
          <Text fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
            Sistem Manajemen
          </Text>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Main Navigation */}
        <VStack align="stretch" spacing={1}>
          <SidebarItem
            icon={FiHome}
            label="Dashboard"
            isActive={activeItem === "dashboard" && !location.pathname.startsWith('/admin/sakti')}
            onClick={() => handleItemClick("dashboard")}
          />
          <SidebarItem
            icon={FiBarChart2}
            label="Sakti"
            isActive={location.pathname.startsWith('/admin/sakti')}
            onClick={() => navigate('/admin/sakti')}
          />
        </VStack>

        {/* Data Management */}
        <SidebarGroup
          title="Manajemen Data"
          icon={FiDatabase}
          defaultIsOpen={false}
          items={[
            {
              icon: FiGrid,
              label: "Kategori",
              isActive: activeItem === "categories",
              badge: badgeCounts.categories,
              onClick: () => handleItemClick("categories"),
            },
            {
              icon: FiShield,
              label: "Penalti",
              isActive: activeItem === "penalties",
              onClick: () => handleItemClick("penalties"),
            },
            {
              icon: FiMapPin,
              label: "Estate",
              isActive: activeItem === "estates",
              onClick: () => handleItemClick("estates"),
            },
            {
              icon: FiMapPin,
              label: "Divisi",
              isActive: activeItem === "divisis",
              onClick: () => handleItemClick("divisis"),
            },
            {
              icon: FiMapPin,
              label: "Blok",
              isActive: activeItem === "bloks",
              onClick: () => handleItemClick("bloks"),
            },
            {
              icon: FiBriefcase,
              label: "Job Level",
              isActive: activeItem === "job-levels",
              onClick: () => handleItemClick("job-levels"),
            },
            {
              icon: FiUsers,
              label: "Foreman Groups",
              isActive: activeItem === "foreman-groups",
              onClick: () => handleItemClick("foreman-groups"),
            },
            {
              icon: FiUser,
              label: "Karyawan",
              isActive: activeItem === "employees",
              badge: badgeCounts.employees,
              onClick: () => handleItemClick("employees"),
            },
            {
              icon: FiRefreshCw,
              label: "Sinkronisasi Data Master",
              isActive: activeItem === "master-sync",
              onClick: () => handleItemClick("master-sync"),
            },
          ]}
        />

        {/* Inspection Management */}
        <SidebarGroup
          title="Manajemen Inspeksi"
          icon={FiActivity}
          defaultIsOpen={false}
          items={[
            {
              icon: FiFileText,
              label: "Sinkronisasi Inspeksi",
              isActive: activeItem === "inspections",
              badge: badgeCounts.inspections,
              onClick: () => handleItemClick("inspections"),
            },
            {
              icon: FiClock,
              label: "Riwayat Inspeksi",
              isActive: activeItem === "inspection-history",
              onClick: () => handleItemClick("inspection-history"),
            },
          ]}
        />

        {/* User Management */}
        <SidebarGroup
          title="Manajemen Akses"
          icon={FiShield}
          items={[
            {
              icon: FiLogIn,
              label: "Login Requests",
              isActive: activeItem === "login-requests",
              badge: badgeCounts.loginRequests,
              onClick: () => handleItemClick("login-requests"),
            },
            {
              icon: FiShield,
              label: "Izin Kategori",
              isActive: activeItem === "permissions",
              onClick: () => handleItemClick("permissions"),
            },
            // {
            //   icon: FiUsers,
            //   label: "Role Management",
            //   isActive: activeItem === "roles",
            //   onClick: () => handleItemClick("roles"),
            // },
          ]}
        />

        {/* RBAC Management */}
        <SidebarGroup
          title="RBAC"
          icon={FiShield}
          defaultIsOpen={false}
          items={[
            ...(canAccess('/admin/applications') ? [{
              icon: FiSettings,
              label: 'Applications',
              isActive: location.pathname.startsWith('/admin/applications'),
              onClick: () => navigate('/admin/applications'),
            }] : []),
            ...(canAccess('/admin/menus') ? [{
              icon: FiGrid,
              label: 'Menus',
              isActive: location.pathname.startsWith('/admin/menus'),
              onClick: () => navigate('/admin/menus'),
            }] : []),
            ...(canAccess('/admin/roles') ? [{
              icon: FiUsers,
              label: 'Roles',
              isActive: location.pathname.startsWith('/admin/roles'),
              onClick: () => navigate('/admin/roles'),
            }] : []),
            ...(canAccess('/admin/users') ? [{
              icon: FiUser,
              label: 'Users',
              isActive: location.pathname.startsWith('/admin/users'),
              onClick: () => navigate('/admin/users'),
            }] : []),
          ]}
        />

        {/* Monitoring */}
        <SidebarGroup
          title="Monitoring"
          icon={FiActivity}
          defaultIsOpen={false}
          items={[
            {
              icon: FiClock,
              label: "Login Log",
              isActive: activeItem === "audit-logs",
              badge: badgeCounts.auditLogs,
              onClick: () => handleItemClick("audit-logs"),
            },
            {
              icon: FiBarChart2,
              label: "Aktivitas Menu",
              isActive: activeItem === "activity-logs",
              onClick: () => handleItemClick("activity-logs"),
            },
          ]}
        />

        {/* System */}
        <SidebarGroup
          title="Sistem"
          icon={FiSettings}
          items={[
            {
              icon: FiSettings,
              label: "Pengaturan",
              isActive: activeItem === "settings",
              onClick: () => handleItemClick("settings"),
            },
            {
              icon: FiActivity,
              label: "Log Aktivitas",
              isActive: activeItem === "logs",
              onClick: () => handleItemClick("logs"),
            },
          ]}
        />

        {/* Separator Line */}
        <Divider borderColor={borderColor} />

        {/* Back to Home / Main Dashboard */}
        <Button
          variant="ghost"
          justifyContent="flex-start"
          leftIcon={<FiArrowLeft />}
          onClick={() => navigate('/')}
          color={useColorModeValue("gray.600", "gray.400")}
          _hover={{ bg: useColorModeValue("gray.100", "gray.700"), color: useColorModeValue("gray.800", "gray.100") }}
          borderRadius="lg"
          py={3}
          h="auto"
        >
          Back to Dashboard
        </Button>
      </VStack>
    </Box>
  );
};

export default AdminSidebar;
